import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Edit2, Bot, CheckCircle2, MessageCircle, X } from "lucide-react";
import { getKnowledgeBase, saveKnowledgeBase, ChatKnowledgeEntry, CHATBOT_CATEGORIES } from "@/data/chatbotKnowledge";
import { isChatbotEnabled, setChatbotEnabled, CHATBOT_ENABLED_KEY, getChatbotPaths, setChatbotPaths, CHATBOT_PATHS_KEY } from "@/components/ChatBotWidget";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";

const AVAILABLE_PATHS = [
  { path: "/", label: "Accueil (Recommandé)" },
  { path: "/voter", label: "Guide de l'Électeur" },
  { path: "/partis", label: "Profils des Partis" },
  { path: "/emissions", label: "Vidéos & Émissions" },
  { path: "/quiz", label: "Quiz Citoyen" },
  { path: "/match", label: "Match ton Parti" },
  { path: "/ring", label: "Le Ring (A/B Test)" },
];

const initialForm: Omit<ChatKnowledgeEntry, "id"> = {
  category: "general",
  keywords: [],
  questionFr: "",
  questionAr: "",
  answerFr: "",
  answerAr: "",
  links: []
};

const AdminChatbotPage = () => {
  const { toast } = useToast();
  
  const [knowledge, setKnowledge] = useState<ChatKnowledgeEntry[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState(initialForm);
  const [newKeyword, setNewKeyword] = useState("");
  const [newLink, setNewLink] = useState({ label: "", url: "" });
  const [chatbotEnabled, setChatbotEnabledState] = useState(true);
  const [chatbotPaths, setChatbotPathsState] = useState<string[]>(['/']);

  useEffect(() => {
    setKnowledge(getKnowledgeBase());
    setChatbotEnabledState(isChatbotEnabled());
    setChatbotPathsState(getChatbotPaths());
    
    // Listen to changes from other tabs/sidebar
    const handler = () => {
      setChatbotEnabledState(isChatbotEnabled());
      setChatbotPathsState(getChatbotPaths());
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const toggleChatbot = (checked: boolean) => {
    setChatbotEnabledState(checked);
    setChatbotEnabled(checked);
    window.dispatchEvent(new StorageEvent("storage", { key: CHATBOT_ENABLED_KEY }));
    toast({ 
      title: checked ? "Chatbot Activé" : "Chatbot Masqué", 
      description: checked ? "La bulle est visible sur le site." : "La bulle est maintenant masquée pour les visiteurs." 
    });
  };

  const togglePath = (path: string, checked: boolean) => {
    const newPaths = checked ? [...chatbotPaths, path] : chatbotPaths.filter(p => p !== path);
    setChatbotPathsState(newPaths);
    setChatbotPaths(newPaths);
    window.dispatchEvent(new StorageEvent("storage", { key: CHATBOT_PATHS_KEY }));
    toast({ title: "Pages d'affichage mises à jour", description: "La configuration d'affichage a été enregistrée." });
  };

  const handleSave = () => {
    if (!formData.questionFr || !formData.answerFr) {
      toast({ title: "Erreur", description: "La question et la réponse en français sont requises.", variant: "destructive" });
      return;
    }

    let updatedKnowledge;
    if (editingId) {
      updatedKnowledge = knowledge.map(k => k.id === editingId ? { ...formData, id: editingId } : k);
      toast({ title: "Modifiée", description: "L'entrée a été modifiée avec succès." });
    } else {
      const newEntry: ChatKnowledgeEntry = {
        id: `kb_${Date.now()}`,
        ...formData
      };
      updatedKnowledge = [newEntry, ...knowledge];
      toast({ title: "Créée", description: "Nouvelle entrée ajoutée à la base de connaissances." });
    }
    
    setKnowledge(updatedKnowledge);
    saveKnowledgeBase(updatedKnowledge);
    
    setShowForm(false);
    setEditingId(null);
    setFormData(initialForm);
  };

  const handleDelete = (id: string) => {
    const updated = knowledge.filter(k => k.id !== id);
    setKnowledge(updated);
    saveKnowledgeBase(updated);
    toast({ title: "Supprimée", description: "L'entrée a été supprimée." });
  };

  const handleEdit = (entry: ChatKnowledgeEntry) => {
    setFormData({ ...entry, links: entry.links || [] });
    setEditingId(entry.id);
    setShowForm(true);
  };

  const addKeyword = () => {
    if (newKeyword && !formData.keywords.includes(newKeyword)) {
      setFormData({ ...formData, keywords: [...formData.keywords, newKeyword.toLowerCase()] });
      setNewKeyword("");
    }
  };

  const removeKeyword = (kw: string) => {
    setFormData({ ...formData, keywords: formData.keywords.filter(k => k !== kw) });
  };

  const addLink = () => {
    if (newLink.label && newLink.url) {
      setFormData({ ...formData, links: [...(formData.links || []), newLink] });
      setNewLink({ label: "", url: "" });
    }
  };

  const removeLink = (index: number) => {
    setFormData({ ...formData, links: (formData.links || []).filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-card p-6 rounded-2xl shadow-sm border border-border">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground flex items-center gap-2">
            <Bot className="w-8 h-8 text-emerald-500" /> Gestion Kayn'Bot
          </h1>
          <p className="text-muted-foreground font-body">Gérez la base de connaissances et les réponses du Chatbot interactif.</p>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-4 border-t sm:border-t-0 sm:border-l border-border pt-4 sm:pt-0 sm:pl-6">
          <div className="flex items-center gap-3">
            <Switch 
              checked={chatbotEnabled} 
              onCheckedChange={toggleChatbot} 
              className={chatbotEnabled ? "data-[state=checked]:bg-emerald-500" : ""}
            />
            <div className="flex flex-col">
              <span className="text-sm font-bold leading-none">{chatbotEnabled ? "Actif (Visible)" : "Masqué (En pause)"}</span>
              <span className="text-[10px] text-muted-foreground mt-1 text-center sm:text-left">Statut de la bulle Kayn'Bot</span>
            </div>
          </div>
          <Button onClick={() => { setFormData(initialForm); setEditingId(null); setShowForm(!showForm); }} className="gap-2 font-semibold whitespace-nowrap">
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "Annuler" : "Nouvelle Entrée"}
          </Button>
        </div>
      </div>

      <div className="bg-card p-6 rounded-2xl shadow-sm border border-border mt-6">
        <h2 className="font-semibold text-lg mb-4 text-foreground">Emplacements d'affichage du Chatbot</h2>
        <p className="text-sm text-muted-foreground mb-4">Sélectionnez les pages où la bulle du Kayn'Bot doit apparaître. Par défaut, il s'affiche sur la page d'accueil.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {AVAILABLE_PATHS.map(item => (
            <label key={item.path} className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-muted/50 transition-colors">
              <Checkbox 
                 checked={chatbotPaths.includes(item.path)}
                 onCheckedChange={(c) => togglePath(item.path, !!c)}
                 className="mt-0.5"
              />
              <span className="text-sm font-medium leading-tight">{item.label}<br/><span className="text-[10px] text-muted-foreground font-mono">{item.path}</span></span>
            </label>
          ))}
        </div>
      </div>

      {showForm && (
        <Card className="border-emerald-500/20 shadow-lg">
          <CardHeader className="bg-muted/30 border-b border-border/50">
            <CardTitle>{editingId ? "Modifier l'entrée" : "Nouvelle Entrée dans la Base"}</CardTitle>
            <CardDescription>Définissez les mots-clés qui déclencheront cette réponse.</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Label>Catégorie</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger><SelectValue placeholder="Catégorie" /></SelectTrigger>
                  <SelectContent>
                    {CHATBOT_CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.labelFr}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label>Mots-clés (Appuyez sur Entrée)</Label>
                <div className="flex gap-2">
                  <Input 
                    value={newKeyword} 
                    onChange={e => setNewKeyword(e.target.value)} 
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                    placeholder="Ex: voter, تصويت" 
                  />
                  <Button type="button" onClick={addKeyword} variant="secondary">Ajouter</Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.keywords.map(kw => (
                    <Badge key={kw} variant="secondary" className="gap-1 px-2 py-1">
                      {kw} <button onClick={() => removeKeyword(kw)}><X className="w-3 h-3 hover:text-destructive" /></button>
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Label>Question type (Français)</Label>
                <Input value={formData.questionFr} onChange={e => setFormData({ ...formData, questionFr: e.target.value })} placeholder="Qui peut voter ?" />
              </div>
              <div className="space-y-3">
                <Label>Question type (Darija/Arabe)</Label>
                <Input dir="rtl" value={formData.questionAr} onChange={e => setFormData({ ...formData, questionAr: e.target.value })} placeholder="شكون يقدر يصوت؟" />
              </div>

              <div className="space-y-3">
                <Label>Réponse (Français)</Label>
                <Textarea value={formData.answerFr} onChange={e => setFormData({ ...formData, answerFr: e.target.value })} placeholder="La réponse du bot..." className="min-h-[150px]" />
              </div>
              <div className="space-y-3">
                <Label>Réponse (Darija/Arabe)</Label>
                <Textarea dir="rtl" value={formData.answerAr} onChange={e => setFormData({ ...formData, answerAr: e.target.value })} placeholder="الجواب بالدارجة..." className="min-h-[150px]" />
              </div>

              <div className="space-y-3 md:col-span-2">
                <Label>Liens recommandés (Optionnel)</Label>
                <div className="flex gap-2 items-end">
                  <div className="space-y-1 flex-1">
                    <Label className="text-xs">Libellé</Label>
                    <Input value={newLink.label} onChange={e => setNewLink({ ...newLink, label: e.target.value })} placeholder="Label du bouton" />
                  </div>
                  <div className="space-y-1 flex-1">
                    <Label className="text-xs">URL</Label>
                    <Input value={newLink.url} onChange={e => setNewLink({ ...newLink, url: e.target.value })} placeholder="/lien-interne ou https://..." />
                  </div>
                  <Button type="button" onClick={addLink} variant="secondary">Ajouter lien</Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(formData.links || []).map((link, idx) => (
                    <Badge key={idx} variant="outline" className="gap-2 py-1">
                      {link.label} ({link.url})
                      <button onClick={() => removeLink(idx)}><X className="w-3 h-3 hover:text-destructive" /></button>
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-4 pt-6 mt-4 border-t border-border">
              <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700">
                <CheckCircle2 className="w-4 h-4 mr-2" /> {editingId ? "Mettre à jour" : "Sauvegarder l'entrée"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Liste des entrées */}
      <div className="grid gap-4 mt-6">
        {knowledge.length === 0 ? (
          <div className="text-center py-20 bg-card rounded-2xl border border-border">
            <Bot className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-xl font-display font-medium text-foreground">Base de connaissances vide</h3>
            <p className="text-muted-foreground mt-2">Ajoutez des questions/réponses pour rendre votre chatbot intelligent.</p>
          </div>
        ) : (
           knowledge.map(entry => (
            <Card key={entry.id} className="transition-all hover:border-emerald-500/30">
              <div className="p-5 flex flex-col md:flex-row gap-4 justify-between">
                
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="secondary" className="capitalize">Catégorie: {entry.category}</Badge>
                    <span className="text-xs text-muted-foreground">{entry.keywords.length} mots-clés</span>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-bold text-sm text-foreground mb-1"><MessageCircle className="inline w-3 h-3 mr-1 text-primary"/>{entry.questionFr}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2">{entry.answerFr}</p>
                    </div>
                    <div dir="rtl" className="text-right">
                      <p className="font-bold text-sm text-foreground mb-1"><MessageCircle className="inline w-3 h-3 ml-1 text-primary"/>{entry.questionAr}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2">{entry.answerAr}</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-1 mt-3">
                    {entry.keywords.slice(0, 5).map(kw => (
                      <span key={kw} className="text-[10px] bg-muted px-2 py-0.5 rounded text-muted-foreground">#{kw}</span>
                    ))}
                    {entry.keywords.length > 5 && <span className="text-[10px] bg-muted px-2 py-0.5 rounded text-muted-foreground">+{entry.keywords.length - 5}</span>}
                  </div>
                </div>

                <div className="flex items-start gap-2 justify-end">
                   <Button variant="ghost" size="icon" onClick={() => handleEdit(entry)} className="text-blue-500 hover:text-blue-600 hover:bg-blue-50/50">
                     <Edit2 className="w-4 h-4" />
                   </Button>
                   <Button variant="ghost" size="icon" onClick={() => handleDelete(entry.id)} className="text-destructive hover:bg-destructive/10">
                     <Trash2 className="w-4 h-4" />
                   </Button>
                 </div>
              </div>
            </Card>
           ))
        )}
      </div>
    </div>
  );
};

export default AdminChatbotPage;
